// import { useState } from "react";
import { useState, useEffect, useRef } from "react";
import { ArrowUp, Sparkles } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const SUGGESTIONS = [
  "Summarise what changed this week",
  "Which tenders should I act on first?",
  "Compare my top news items",
];

export default function AskAiWidget() {
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const containerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const ALLOWED = ["/app", "/app/feed", "/app/tenders"];

  useEffect(() => {
    setIsOpen(false);
    setFocused(false);
    setQ("");
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setFocused(false);
        setQ("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen]);

  if (!ALLOWED.includes(location.pathname)) return null;

  const go = (question) => {
    const text = (question ?? q).trim();

    if (!text) return;

    setQ("");
    setFocused(false);

    navigate(
      `/app/ai-analysis?new=1&q=${encodeURIComponent(text)}`
    );
  };

  const openAi = () => {
    setIsOpen(true);
    setShowTooltip(false);
  };

  const closeAi = () => {
    setIsOpen(false);
    setFocused(false);
    setQ("");
  };

  return (
    <>
      {/* =========================
          CENTERED EXPANDED INPUT
      ========================== */}
      {isOpen && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[560px] max-w-[calc(100vw-2rem)]"
          data-testid="ask-ai-expanded" ref={containerRef}
        >
          {/* Suggestions */}
          {focused && (
            <div
              className="absolute bottom-[calc(100%+10px)] left-0 flex flex-col items-start gap-2"
              data-testid="ask-ai-suggestions"
            >
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => go(s)}
                  data-testid={`ask-ai-suggestion-${i}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#0F1F3D]/90 backdrop-blur text-white text-xs font-medium shadow-md hover:bg-[#0F1F3D] transition-colors whitespace-nowrap"
                >
                  <Sparkles className="h-3 w-3 opacity-80" />
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              go();
            }}
            className="flex items-center border !border-[1.2px] !border-[#1c8aeb] gap-2 pl-5 pr-2 py-2 rounded-full bg-white shadow-2xl"
            data-testid="ask-ai-bar"
          >
            <Sparkles className="h-4 w-4 text-[#2563EB] shrink-0" />

            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder="Ask anything about your data..."
              data-testid="ask-ai-input"
              className="flex-1 bg-transparent text-sm text-[#0F1F3D] placeholder:text-[#94A3B8] focus:outline-none min-w-0"
            />

            <button
              type="submit"
              disabled={!q.trim()}
              data-testid="ask-ai-submit"
              aria-label="Ask AI"
              className="h-9 w-9 shrink-0 flex items-center justify-center rounded-full bg-[#0F1F3D] text-white hover:bg-[#172A4F] disabled:opacity-40 transition-colors"
            >
              <ArrowUp className="h-4 w-4" />
            </button>

            {/* Close */}
            <button
              type="button"
              onClick={closeAi}
              aria-label="Close Ask AI"
              className="h-9 w-9 shrink-0 flex items-center justify-center rounded-full bg-[#E2E8F0] text-[#475569] hover:bg-[#CBD5E1] transition-colors"
            >
              ×
            </button>
          </form>
        </div>
      )}

      {/* =========================
          BOTTOM-RIGHT SPARKLES
      ========================== */}
      {!isOpen && (
        <div
          className="fixed bottom-6 right-12 z-40"
          data-testid="ask-ai-widget"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {/* Tooltip */}
          {showTooltip && (
            <div
              className="absolute bottom-[calc(100%+12px)] right-0 w-[220px] rounded-xl border border-[#E2E8F0] bg-white px-4 py-3.5 shadow-[0_8px_24px_rgba(15,31,61,0.14)]"
              data-testid="ask-ai-tooltip"
            >
              <div className="text-sm font-medium text-[#0F1F3D]">
                Ask anything
              </div>

              <div className="mt-1 text-xs leading-5 text-[#64748B]">
                Ask questions and explore insights
                from your data.
              </div>

              <div className="mt-2 text-xs font-medium text-[#2563EB]">
                Explore AI analysis →
              </div>

              {/* Tooltip arrow */}
              <div className="absolute -bottom-1.5 right-5 h-3 w-3 rotate-45 border-r border-b border-[#E2E8F0] bg-white" />
            </div>
          )}

          <button
            type="button"
            onClick={openAi}
            aria-label="Ask anything"
            data-testid="ask-ai-button"
            className="h-12 w-12 flex items-center justify-center rounded-full bg-[#0F1F3D] text-white shadow-xl hover:bg-[#172A4F] hover:scale-105 transition-all duration-200"
          >
            <Sparkles className="h-5 w-5" />
          </button>
        </div>
      )}
    </>
  );
}